import {
  Component,
  Input,
  ViewChild,
  OnInit,
  forwardRef,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { QuestionBase } from 'core/components/forms';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from 'core/services/profile.service';
import { ModalComponent } from 'core/components/modal/modal.component';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { environment } from 'environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'lms-courses-selector',
  templateUrl: './courses-selector.component.html',
  styleUrls: ['./courses-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoursesSelectorComponent),
      multi: true
    }
  ]
})
export class CoursesSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() courses: any[];
  @Input() imageUrl?: string = '';
  @Input() itineraryCourses?: any[];
  @Input() unavailableCourses: any[];
  @Input() hideButtons: boolean = false;
  @Input() title: string = 'actions.selectedCourse';
  @Output() coursesSelected: EventEmitter<any[]> = new EventEmitter<any[]>();
  @ViewChild('modalCoursesSelector', { static: false })
  public modalCoursesSelector: ModalComponent;
  @ViewChild('modalCoursesDelete', { static: false })
  public modalCoursesDelete: ModalComponent;
  @ViewChild('modalCoursesImageDelete', { static: false })
  public modalCoursesImageDelete: ModalComponent;
  public selectedCourses: any[] = [];
  public duration: string;
  public tempCourses: any[];
  public voidCourse: boolean = false;
  public selectedCourseToDelete: any;
  resume: string;
  editData: any;
  public questions: QuestionBase<any>[] = [];
  id = `lms-courses-selector-${Date.now()}`;
  focus = false;
  isDisabled: false;

  public propagateChange = (_: any) => {};
  public propagateTouched = (_: any) => {};

  constructor(public profileService: ProfileService, public translate: TranslateService) {
    this.imageUrl =
      'https://min.gamelearn.io/css-resources/gamelearn/resources/campus/game-cards/bg_game_card_default01.jpg';
  }

  writeValue(value: any) {
    this.itineraryCourses = [...value];
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.courses.currentValue) {
      this.courses = this.courses.filter((course) => !course.archived && !course.withdrawn);
      this.tempCourses = [...this.courses];
    }
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn) {
    this.propagateTouched = fn;
  }

  getImageUrl(course) {
    return course ? `${environment.RESOURCES_HOST}${course.cardImageUrl}` : this.imageUrl;
  }

  deleteCourse(course) {
    this.itineraryCourses = this.itineraryCourses.filter((c) => course.id !== c.id);
    this.selectedCourseToDelete = course;
    this.modalCoursesDelete.open();
    this.propagate();
  }

  openCoursesSelector() {
    this.selectedCourses = this.itineraryCourses.map((course) => course);
    this.modalCoursesSelector.open();
  }

  changeCourseOrder(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.itineraryCourses, event.previousIndex, event.currentIndex);
    this.propagate();
  }

  selectCourse(course: any) {
    const foundCourse = this.getChecked(course);
    if (foundCourse) {
      this.selectedCourses = this.selectedCourses.filter((selectedCourse) => selectedCourse.id !== course.id);
    } else {
      this.selectedCourses.push(course);
    }
  }

  getChecked(course) {
    return !!this.selectedCourses.find((selectedCourse) => course.id === selectedCourse.id);
  }

  public searchWords(words: string) {
    this.tempCourses = this.search(words);
  }

  private search(words: string): any[] {
    let tempCourses = [];
    if (!words) {
      return [...this.courses];
    } else {
      tempCourses = this.courses.filter(
        (courses: any) =>
          (courses.title && courses.title.toLowerCase().includes(words.toLowerCase())) ||
          (courses.topic && courses.topic.toLowerCase().includes(words.toLowerCase())) ||
          (courses.hours && courses.hours === parseFloat(words))
      );
    }
    return tempCourses;
  }

  selectCourses() {
    this.itineraryCourses = this.selectedCourses.map((course) => {
      return {
        id: course.id,
        name: course.name,
        topic: course.topic,
        hours: course.hours,
        minutes: course.minutes,
        gameName: course.gameName,
        clientName: course.clientName || course.creator,
        cardImageUrl: course.cardImageUrl
          ? course.cardImageUrl
          : course['cardImage']
          ? course['cardImage']['url']
          : this.imageUrl,
        cardImageId: course.cardImageId,
        bannerUrl: course.bannerUrl ? course.bannerUrl : course['banner'] ? course['banner']['url'] : this.imageUrl,
        bannerId: course.bannerId,
        accessMode: course.accessMode ? course.accessMode : 'open'
      };
    });

    this.propagate();
    this.modalCoursesSelector.close();
  }

  private propagate() {
    this.coursesSelected.emit(this.itineraryCourses);
    this.propagateChange(this.itineraryCourses);
  }

  confirmDelete() {
    this.itineraryCourses = this.itineraryCourses.filter((course) => this.selectedCourseToDelete.id !== course.id);
    this.propagateChange(this.itineraryCourses);
    this.modalCoursesDelete.close();
  }
}
